import React from 'react';
import { Check, X, Database, ArrowRightLeft, FileCheck, ShieldAlert, Server, HardDrive, History, Repeat, CheckCircle2, Clock, Star } from 'lucide-react';

const Migration: React.FC = () => {
  const serviceAreas = [
    {
      icon: <Database size={32} />,
      title: "Auditoría de Datos",
      description: "Análisis exhaustivo de tu plataforma actual para identificar volúmenes de datos, versiones de plugins, personalizaciones y posibles incompatibilidades.",
      benefit: "Diagnóstico claro y plan de migración libre de sorpresas."
    },
    {
      icon: <Server size={32} />,
      title: "Migración de Hosting",
      description: "Traslado completo de tu LMS de un proveedor a otro o hacia nuestra infraestructura optimizada, asegurando la configuración correcta del servidor destino.",
      benefit: "Mejora de rendimiento inmediata sin perder información."
    },
    {
      icon: <ArrowRightLeft size={32} />,
      title: "Actualización de Versiones",
      description: "Upgrade seguro de versiones antiguas de Moodle (ej. 3.9 a 4.x), manejando cambios en la estructura de base de datos y compatibilidad de temas.",
      benefit: "Acceso a nuevas funcionalidades y parches de seguridad manteniendo tus cursos históricos."
    },
    {
      icon: <Repeat size={32} />,
      title: "Consolidación de Plataformas",
      description: "Unificación de múltiples instalaciones Moodle dispersas en una sola instancia centralizada, fusionando bases de usuarios y categorías.",
      benefit: "Gestión centralizada y reducción de costos operativos y de mantenimiento."
    },
    {
      icon: <FileCheck size={32} />,
      title: "Validación de Integridad",
      description: "Pruebas rigurosas post-migración para verificar que cada curso, calificación, tarea y usuario se haya transferido correctamente.",
      benefit: "Garantía de que la operación académica continuará sin contratiempos."
    },
    {
      icon: <ShieldAlert size={32} />,
      title: "Plan de Contingencia",
      description: "Estrategias de rollback y operación paralela durante la transición para eliminar riesgos de pérdida de servicio crítico.",
      benefit: "Seguridad operativa durante todo el proceso de cambio."
    }
  ];

  const plans = [
    {
      name: "Upgrade",
      price: "Desde $450.000",
      period: "/evento",
      description: "Actualización de versión en mismo servidor",
      popular: false,
      features: [
        { text: "Backup completo previo", included: true },
        { text: "Actualización de Core Moodle", included: true },
        { text: "Actualización de Plugins", included: true },
        { text: "Adaptación de Tema Básico", included: true },
        { text: "Pruebas de funcionalidad", included: true },
        { text: "Informe de cambios", included: true },
        { text: "Corrección de datos corruptos", included: false },
        { text: "Optimización de base de datos", included: false }
      ],
      buttonText: "Cotizar Upgrade",
      buttonClass: "btn-secondary"
    },
    {
      name: "Migración Full",
      price: "Desde $850.000",
      period: "/evento",
      description: "Traslado a nuevo servidor + Optimización",
      popular: true,
      features: [
        { text: "Auditoría de infraestructura", included: true },
        { text: "Transferencia de archivos y BD", included: true },
        { text: "Configuración de nuevo servidor", included: true },
        { text: "Optimización de rendimiento", included: true },
        { text: "Configuración DNS y SSL", included: true },
        { text: "Garantía de integridad", included: true },
        { text: "Ventana de mantenimiento nocturna", included: true },
        { text: "Soporte post-migración (7 días)", included: true }
      ],
      buttonText: "Solicitar Migración",
      buttonClass: "btn-primary"
    },
    {
      name: "Consolidación",
      price: "A medida",
      period: "/proyecto",
      description: "Unificación de múltiples LMS",
      popular: false,
      features: [
        { text: "Análisis de múltiples fuentes", included: true },
        { text: "Fusión de bases de usuarios", included: true },
        { text: "Reestructuración de categorías", included: true },
        { text: "Normalización de datos", included: true },
        { text: "Desarrollo de scripts a medida", included: true },
        { text: "Pruebas de carga", included: true },
        { text: "Plan de comunicación a usuarios", included: true },
        { text: "Gerencia de proyecto dedicada", included: true }
      ],
      buttonText: "Agendar Reunión",
      buttonClass: "btn-secondary"
    }
  ];

  const benefits = [
    {
      icon: <CheckCircle2 size={24} />,
      title: "Integridad de Datos",
      description: "Ni un solo registro, nota o tarea perdida en el proceso"
    },
    {
      icon: <Clock size={24} />,
      title: "Mínimo Downtime",
      description: "Ejecución en horarios inhábiles para no afectar clases"
    },
    {
      icon: <History size={24} />,
      title: "Historial Completo",
      description: "Conservamos logs y registros históricos de años anteriores"
    },
    {
      icon: <HardDrive size={24} />,
      title: "Limpieza",
      description: "Aprovechamos para limpiar archivos temporales y basura"
    }
  ];

  const useCases = [
    {
      title: "Obsolescencia Tecnológica",
      description: "Instituciones con Moodle 3.5 o inferior que necesitan actualizarse a Moodle 4+ por seguridad y funcionalidades visuales.",
      results: "Plataforma moderna y segura"
    },
    {
      title: "Cambio de Proveedor",
      description: "Clientes insatisfechos con su hosting actual que buscan mejor soporte y rendimiento sin perder su trabajo acumulado.",
      results: "Mejor experiencia de usuario inmediata"
    },
    {
      title: "Fusión de Sedes",
      description: "Universidades con plataformas independientes por facultad o sede que deciden centralizar la gestión académica.",
      results: "Control administrativo unificado"
    }
  ];

  return (
    <section id="migration" className="migration-landing">
      {/* Hero Section */}
      <div className="crm-hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-left">
              <div className="hero-badge">
                <Database size={16} />
                Seguridad de Datos
              </div>
              <h1 className="hero-title">
                Migración y Consolidación
                <span className="highlight">Sin Pérdida de Datos</span>
              </h1>
              <p className="hero-description">
                Trasladamos, actualizamos y unificamos tus plataformas educativas con metodologías probadas. 
                Garantizamos la integridad de tu información y la continuidad operativa.
              </p>
              <div className="hero-cta">
                <a href="#contacto" className="btn btn-primary btn-lg">
                  Evaluar mi Migración
                </a>
                <a href="#planes" className="btn btn-secondary btn-lg">
                  Ver Opciones
                </a>
              </div>
            </div>
            <div className="hero-right">
              <div className="hero-stats">
                <div className="stat-card">
                  <div className="stat-number">100%</div>
                  <div className="stat-label">Integridad de datos</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">0</div>
                  <div className="stat-label">Incidentes críticos</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">+200</div>
                  <div className="stat-label">Sitios migrados</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Service Areas */}
      <div className="container">
        <div className="service-areas">
          <div className="section-header">
            <h2>Procesos Transparentes y Seguros</h2>
            <p className="section-subtitle">
              Entendemos que la información histórica es el activo más valioso de tu institución
            </p>
          </div>
          
          <div className="service-grid">
            {serviceAreas.map((area, index) => (
              <div key={index} className="service-card">
                <div className="service-icon">
                  {area.icon}
                </div>
                <h4>{area.title}</h4>
                <p className="service-description">{area.description}</p>
                <div className="service-benefit">
                  <strong>Beneficio:</strong> {area.benefit}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits */}
        <div className="benefits-section">
          <h3>Tranquilidad en procesos críticos</h3>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <div className="benefit-icon">{benefit.icon}</div>
                <h4>{benefit.title}</h4>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Use Cases */}
        <div className="use-cases-section">
          <div className="section-header">
            <h2>Escenarios Comunes</h2>
            <p className="section-subtitle">
              Resolvemos desafíos complejos de infraestructura de datos
            </p>
          </div>
          <div className="use-cases-grid">
            {useCases.map((useCase, index) => (
              <div key={index} className="use-case-card">
                <h4>{useCase.title}</h4>
                <p className="use-case-description">{useCase.description}</p>
                <div className="use-case-results">
                  <Check size={20} className="check-icon" />
                  <span>{useCase.results}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing Plans */}
        <div id="planes" className="pricing-section">
          <div className="section-header">
            <h2>Planes de Servicio</h2>
            <p className="section-subtitle">
              Inversión única para asegurar el futuro de tu plataforma
            </p>
          </div>
          
          <div className="pricing-grid">
            {plans.map((plan, index) => (
              <div key={index} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
                {plan.popular && (
                  <div className="popular-badge">
                    <Star size={16} />
                    Recomendado
                  </div>
                )}
                
                <div className="pricing-header">
                  <h4>{plan.name}</h4>
                  <div className="price">
                    <span className="amount">{plan.price}</span>
                    <span className="period">{plan.period}</span>
                  </div>
                  <p className="plan-description">{plan.description}</p>
                </div>

                <div className="features-list">
                  {plan.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="feature-item">
                      {feature.included ? (
                        <Check size={20} className="feature-check" />
                      ) : (
                        <X size={20} className="feature-cross" />
                      )}
                      <span className={feature.included ? '' : 'feature-disabled'}>
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="pricing-footer">
                  <a href="#contacto" className={`btn ${plan.buttonClass} btn-block`}>
                    {plan.buttonText}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="final-cta">
          <div className="cta-content">
            <h3>¿Planeando un cambio importante?</h3>
            <p>
              No arriesgues tus datos. Hablemos sobre cómo ejecutar tu migración 
              de forma profesional y segura.
            </p>
            <div className="cta-buttons">
              <a href="#contacto" className="btn btn-primary btn-lg">
                Solicitar Diagnóstico
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Migration;
